import { ReactNode } from "react";
import { Switch } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import BillingStartField from "features/billing-period/billing-start-field";
import ContractTermField, {
  ContractTerm,
  termFromEndDate,
} from "features/billing-period/contract-term-field";
import {
  BillingPeriodMode,
  ScheduleMode,
  defaultBillingPeriodMode,
  defaultBillingStartMonth,
  isValidScheduleDay,
  toMonthInput,
} from "features/billing-period/billing-period";
import Notice from "components/tw/notice";
import {
  errorTextCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";

/** How a contract is invoiced; what the order page's "Sąskaitos" section edits. */
export type BillingSettings = {
  /** A long-term contract: one invoice every month. */
  isRecurring: boolean;
  term: ContractTerm;
  scheduleMode: ScheduleMode;
  /** "1"–"28", typed as text; only used with `specific_day`. */
  scheduleDay: string;
  periodMode: BillingPeriodMode;
  /** `YYYY-MM`; empty = the server's default (see defaultBillingStartMonth). */
  startMonth: string;
};

export const NO_BILLING: BillingSettings = {
  isRecurring: false,
  term: termFromEndDate(""),
  scheduleMode: "last_day_of_month",
  scheduleDay: "1",
  periodMode: "current_month",
  startMonth: "",
};

export function isScheduleDayInvalid(billing: BillingSettings): boolean {
  return (
    billing.isRecurring &&
    billing.scheduleMode === "specific_day" &&
    !isValidScheduleDay(billing.scheduleDay)
  );
}

type Props = {
  billing: BillingSettings;
  onChange: (next: BillingSettings) => void;
  /** The user may not change billing (no permission, or the order is closed). */
  disabled: boolean;
  canManage: boolean;
  isClosed: boolean;
  isExternal: boolean;
  termError: string;
  /** What the default billing start depends on; unknown parts for an order not saved yet. */
  order: {
    date: string;
    createdAt: string | null;
    capPeriods: Record<BillingPeriodMode, string> | null;
  };
  /** The start month as loaded — an unchanged start warns about nothing. */
  loadedStartMonth: string;
};

function Field({
  id,
  label,
  help,
  error,
  children,
}: {
  id: string;
  label: string;
  help?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
      {help && <p className={helpTextCls}>{help}</p>}
      {error && <p className={errorTextCls}>{error}</p>}
    </div>
  );
}

export default function InvoicesSection({
  billing,
  onChange,
  disabled,
  canManage,
  isClosed,
  isExternal,
  termError,
  order,
  loadedStartMonth,
}: Props) {
  const { t } = useTranslation();
  const capPeriod = order.capPeriods?.[billing.periodMode];

  /** A new schedule brings its own default period mode, as on the server. */
  const changeScheduleMode = (scheduleMode: ScheduleMode) =>
    onChange({
      ...billing,
      scheduleMode,
      periodMode: defaultBillingPeriodMode(scheduleMode),
    });

  return (
    <>
      <div className="flex items-start gap-3">
        <Switch
          checked={billing.isRecurring}
          onChange={(isRecurring) => onChange({ ...billing, isRecurring })}
          disabled={disabled}
          className={`${
            billing.isRecurring ? "bg-indigo-600" : "bg-zinc-200 dark:bg-zinc-700"
          } relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <span
            className={`${
              billing.isRecurring ? "translate-x-5" : "translate-x-1"
            } inline-block size-4 rounded-full bg-white transition`}
          />
        </Switch>
        <div>
          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
            {t("order_is_recurring")}
          </p>
          <p className="mt-0.5 text-xs text-zinc-500">{t("order_is_recurring_help")}</p>
        </div>
      </div>

      {!canManage && (
        <p className="text-sm text-zinc-500">{t("invoice_settings_permission_error")}</p>
      )}
      {isClosed && (
        <Notice tone="info">
          <p>{t("order_closed_billing_locked_hint")}</p>
        </Notice>
      )}

      {billing.isRecurring && (
        <div className="space-y-4">
          <Field id="order-schedule-mode" label={t("order_invoice_schedule_mode")}>
            <select
              id="order-schedule-mode"
              className={inputCls}
              value={billing.scheduleMode}
              onChange={(e) => changeScheduleMode(e.target.value as ScheduleMode)}
              disabled={disabled}
            >
              <option value="last_day_of_month">
                {t("order_invoice_schedule_last_day")}
              </option>
              <option value="specific_day">
                {t("order_invoice_schedule_specific_day")}
              </option>
            </select>
          </Field>
          {billing.scheduleMode === "specific_day" && (
            <Field
              id="order-schedule-day"
              label={t("order_invoice_schedule_day")}
              error={
                isScheduleDayInvalid(billing)
                  ? t("order_invoice_schedule_day_invalid")
                  : undefined
              }
            >
              <input
                id="order-schedule-day"
                className={inputCls}
                type="number"
                min={1}
                max={28}
                step={1}
                autoComplete="off"
                aria-invalid={isScheduleDayInvalid(billing)}
                value={billing.scheduleDay}
                onChange={(e) =>
                  onChange({ ...billing, scheduleDay: e.target.value })
                }
                disabled={disabled}
              />
            </Field>
          )}
          <Field
            id="order-period-mode"
            label={t("invoice_billing_period_mode")}
            help={
              billing.periodMode === "previous_month"
                ? t("invoice_billing_period_previous_month_help")
                : t("invoice_billing_period_current_month_help")
            }
          >
            <select
              id="order-period-mode"
              className={inputCls}
              value={billing.periodMode}
              onChange={(e) =>
                onChange({
                  ...billing,
                  periodMode: e.target.value as BillingPeriodMode,
                })
              }
              disabled={disabled}
            >
              <option value="current_month">
                {t("invoice_billing_period_current_month")}
              </option>
              <option value="previous_month">
                {t("invoice_billing_period_previous_month")}
              </option>
            </select>
          </Field>
          <BillingStartField
            value={billing.startMonth}
            onChange={(startMonth) => onChange({ ...billing, startMonth })}
            loadedValue={loadedStartMonth}
            defaultMonth={defaultBillingStartMonth({
              capPeriod,
              orderDate: order.date,
              createdAt: order.createdAt,
            })}
            capMonth={toMonthInput(capPeriod)}
            readOnly={disabled}
          />
          {/* An imported contract may already have been invoiced month by
              month in Finvalda, and those invoices carry no order — so
              nothing here can see them. */}
          {isExternal && (
            <Notice tone="info">
              <p>{t("external_billing_finvalda_hint")}</p>
            </Notice>
          )}
          <ContractTermField
            value={billing.term}
            onChange={(term) => onChange({ ...billing, term })}
            error={termError}
            readOnly={disabled}
          />
        </div>
      )}
    </>
  );
}
